/**
 * シナリオ三点メニュー「セリフとナレーションを入れ替える」のFE純関数
 * (docs/features/scenario-narration-dialogue-swap-plan.md §3.9)。
 *
 * - メニューの有効判定 (sceneHasSwappableText / scenarioHasSwappableText) はBE §3.5と同じ規則
 * - プレビューの状態機械 (useNarrationDialogueSwapPreview) が使う選択条件の正規化・比較
 * - 実行応答の更新後シーン行をlastResultのシナリオへマージする
 */
import type {
  NarrationDialogueSwapNarrationSpeaker,
  NarrationDialogueSwapOptions,
  NarrationDialogueSwapUpdatedScene,
} from '../../../features/scenarios/types';
import { toUiDialogueLines } from './dialogueLines';
import { resolveNarrationText } from './narrationUtils';
import { toUiTelopLines } from './telopLines';
import type { Scenario } from './types';

type UiScene = Scenario['scenes'][number];

export type SwappableSceneSource = Pick<
  UiScene,
  'narration' | 'dialogue' | 'script' | 'dialogue_lines' | 'brand_cut_position' | 'audio_track_type'
>;

/** 本編シーン (ブランドカット・サウンドロゴを除く) か。 */
export function isSwapTargetScene(scene: Pick<UiScene, 'brand_cut_position' | 'audio_track_type'>): boolean {
  if (scene.brand_cut_position) return false;
  if (scene.audio_track_type === 'sound_logo') return false;
  return true;
}

/**
 * このシーンに入れ替えで動かす本文があるか (§3.9・BE §3.5と同じ規則)。
 * - ナレーション本文 (resolveNarrationText: narration → 旧形式のscriptフォールバック) が非空
 * - 構造化シーン (dialogue_linesが配列) で、除外行でない非空textの行がある
 * - レガシーシーン (dialogue_linesがnull) で、フラットdialogueが非空
 * ブランドカット・サウンドロゴのシーンは数えない。
 */
export function sceneHasSwappableText(scene: SwappableSceneSource): boolean {
  if (!isSwapTargetScene(scene)) return false;
  if (resolveNarrationText(scene).trim()) return true;
  const lines = scene.dialogue_lines;
  if (Array.isArray(lines)) {
    return lines.some((line) => !line?.excluded && typeof line?.text === 'string' && line.text.trim() !== '');
  }
  return !!scene.dialogue && scene.dialogue.trim() !== '';
}

/** シナリオのどこかに入れ替えで動かす本文があるか (メニューの有効判定)。 */
export function scenarioHasSwappableText(scenario: { scenes?: readonly SwappableSceneSource[] | null }): boolean {
  return (scenario.scenes ?? []).some(sceneHasSwappableText);
}

/** 話者の値を送信用の形に揃える。名前も俳優も登場人物も無ければ未指定 (null)。 */
function normalizeNarrationSpeaker(
  speaker: (Partial<NarrationDialogueSwapNarrationSpeaker> & Record<string, unknown>) | null | undefined,
): NarrationDialogueSwapNarrationSpeaker | null {
  if (!speaker) return null;
  const label = typeof speaker.label === 'string' ? speaker.label.trim() : '';
  const actorId = speaker.actor_id || null;
  const actorKind = actorId ? (speaker.actor_kind || null) : null;
  const characterKey = actorId ? null : (speaker.character_key || null);
  if (!label && !actorId && !characterKey) return null;
  return { label, actor_id: actorId, actor_kind: actorKind, character_key: characterKey };
}

/**
 * 選択条件を正規化する (キー順固定・allのときはdialogue_speaker_idを落とす・話者の余分なキーを落とす)。
 * 比較 (optionsEqual) と送信の両方にこの形を使う。
 */
export function normalizeSwapOptions(
  options: Partial<NarrationDialogueSwapOptions> | null | undefined,
): NarrationDialogueSwapOptions {
  const scope = options?.dialogue_scope === 'speaker' ? 'speaker' : 'all';
  const speakerId = scope === 'speaker' && options?.dialogue_speaker_id ? options.dialogue_speaker_id : null;
  return {
    dialogue_scope: scope,
    dialogue_speaker_id: speakerId,
    narration_speaker: normalizeNarrationSpeaker(
      options?.narration_speaker as (Partial<NarrationDialogueSwapNarrationSpeaker> & Record<string, unknown>) | null | undefined,
    ),
  };
}

/** 正規化した選択条件の比較キー。 */
export function swapOptionsKey(options: Partial<NarrationDialogueSwapOptions> | null | undefined): string {
  const n = normalizeSwapOptions(options);
  const sp = n.narration_speaker;
  return JSON.stringify([
    n.dialogue_scope,
    n.dialogue_speaker_id ?? null,
    sp ? [sp.label, sp.actor_id, sp.actor_kind, sp.character_key ?? null] : null,
  ]);
}

/** 2つの選択条件が (正規化後に) 等しいか。 */
export function optionsEqual(
  a: Partial<NarrationDialogueSwapOptions> | null | undefined,
  b: Partial<NarrationDialogueSwapOptions> | null | undefined,
): boolean {
  return swapOptionsKey(a) === swapOptionsKey(b);
}

/** dry_runを発行できる選択条件か (speakerモードで話者未選択ならBEが400を返すので発行しない)。 */
export function isSwapOptionsComplete(options: NarrationDialogueSwapOptions): boolean {
  return options.dialogue_scope !== 'speaker' || !!options.dialogue_speaker_id;
}

/**
 * 実行応答の更新後シーン行を画面のシナリオへマージする (§3.9手順6)。
 * id一致のシーンのnarration / dialogue / script / dialogue_lines / telop_lines / narration_material_idを上書きする。
 * dialogue_lines / telop_linesは`[]` (構造化・行なし) をnull (レガシー) に潰さない。
 * narration_material_idがnull (選択解除) なら表示用の派生値 (narration_duration_seconds / narration_voice_id) も落とす。
 */
export function mergeSwapResultIntoScenario<T extends Pick<Scenario, 'scenes'>>(
  scenario: T,
  resultScenes: readonly NarrationDialogueSwapUpdatedScene[],
): T {
  if (!resultScenes.length) return scenario;
  const byId = new Map(resultScenes.map((r) => [r.id, r]));
  let changed = false;
  const scenes = scenario.scenes.map((scene) => {
    const r = byId.get(scene.id);
    if (!r) return scene;
    changed = true;
    const next: UiScene = { ...scene };
    if ('narration' in r) next.narration = r.narration ?? '';
    if ('dialogue' in r) next.dialogue = r.dialogue ?? '';
    if ('script' in r) next.script = r.script ?? '';
    if ('dialogue_lines' in r) next.dialogue_lines = toUiDialogueLines(r.dialogue_lines);
    if ('telop_lines' in r) next.telop_lines = toUiTelopLines(r.telop_lines);
    if ('narration_material_id' in r) {
      if (r.narration_material_id) {
        if (r.narration_material_id !== scene.narration_material_id) {
          next.narration_duration_seconds = undefined;
          next.narration_voice_id = undefined;
        }
        next.narration_material_id = r.narration_material_id;
      } else {
        next.narration_material_id = undefined;
        next.narration_duration_seconds = undefined;
        next.narration_voice_id = undefined;
      }
    }
    return next;
  });
  return changed ? { ...scenario, scenes } : scenario;
}
